export type Stage = 'slug' | 'outline' | 'prose' | 'tts' | 'ready' | 'archived';

export type Vibe = 'adventure' | 'friendship' | 'sleepy' | 'wonder' | 'kindness';

export const VIBE_ORDER: Vibe[] = ['adventure', 'friendship', 'sleepy', 'wonder', 'kindness'];

export const VIBE_LABELS: Record<Vibe, string> = {
  adventure: 'Adventure',
  friendship: 'Friendship',
  sleepy: 'Sleepy',
  wonder: 'Wonder',
  kindness: 'Kindness',
};

export const VIBE_GUIDANCE: Record<Vibe, string> = {
  adventure:
    "An adventure mood — a small expedition or quest, a real place to go, a clear thing to find or accomplish. Energy is brisk but unhurried; obstacles are sized to the universe (a wrong turn, a stretch of effort, a small puzzle).",
  friendship:
    "A friendship mood — center the bond between the characters: a tiny misunderstanding repaired, a small generosity exchanged, or a third character whose arrival tests and confirms what they are to each other.",
  sleepy:
    "A sleepy mood — quiet, slow, hushed. Atmosphere over plot. Mostly a small noticing or a slow settling; the world winds down at the pace of breath.",
  wonder:
    "A wonder mood — something the world reveals: a sky thing, an old mystery, an echo, a question that doesn't fully resolve. The characters encounter awe more than they cause action. Leave room to breathe.",
  kindness:
    "A kindness mood — a small moral, gently held. The story turns on a small choice or recognition (helping when nobody is watching, listening when easier to talk, holding back when easier to push). The lesson is felt, not stated. End on the warmth of the choice, not on a moral declaration.",
};

export type SlugSource = 'batch' | 'chat';

export type Slug = {
  id: string;
  universeId: string;
  title: string;
  hook: string;
  createdAt: number;
  batchId: string;
  vibe?: Vibe;
  guidance?: string;
  source?: SlugSource;
  hidden?: boolean;
};

export type OutlineBeat = {
  id: string;
  heading: string;
  body: string;
};

export type AudioAsset = {
  id: string;
  blobKey: string;
  mimeType: string;
  durationSec?: number;
  voiceId: string;
  createdAt: number;
};

export type RevisionEntry = {
  stage: Stage;
  notes: string;
  at: number;
};

export type Story = {
  id: string;
  universeId: string;
  stage: Stage;
  slug: Slug;
  outline?: OutlineBeat[];
  outlineText?: string;
  proseKey?: string;
  audio?: AudioAsset;
  voiceIdOverride?: string;
  revisionLog: RevisionEntry[];
  createdAt: number;
  updatedAt: number;
  hidden?: boolean;
  inFlight?: Stage;
};

export type PromptTemplateId =
  | 'slug_batch'
  | 'outline_gen'
  | 'outline_revise'
  | 'prose_gen'
  | 'prose_revise'
  | 'story_bible'
  | 'voice_config'
  | 'canon_update';

export type PromptTemplateOverride = {
  id: PromptTemplateId;
  text: string;
  updatedAt: number;
};

export type Settings = {
  model: string;
  ttsModel: string;
  targetWords: number;
  apiBaseUrl?: string;
  apiToken?: string;
};

export const DEFAULT_SETTINGS: Settings = {
  model: 'claude-opus-4-7',
  ttsModel: 'eleven_multilingual_v2',
  targetWords: 800,
};

export const DEFAULT_VOICE_ID = 'nPczCjzI2devNBz1zQrb';

export type Universe = {
  id: string;
  name: string;
  description?: string;
  defaultProtagonists: string;
  defaultVoiceId?: string;
  overrides: Partial<Record<PromptTemplateId, string>>;
  extensions: Partial<Record<PromptTemplateId, string>>;
  batchVibe?: Vibe;
  batchGuidance?: string;
  canon?: string;
  canonInFlight?: boolean;
  canonLastSourceStoryId?: string;
  canonUpdatedAt?: number;
  hidden?: boolean;
  createdAt: number;
  updatedAt: number;
};

export type ChatTextBlock = { type: 'text'; text: string };
export type ChatToolUseBlock = {
  type: 'tool_use';
  id: string;
  name: string;
  input: unknown;
};
export type ChatToolResultBlock = {
  type: 'tool_result';
  tool_use_id: string;
  content: string;
  is_error?: boolean;
  approval?: 'auto' | 'applied' | 'rejected';
};

export type ChatUserBlock = ChatTextBlock | ChatToolResultBlock;
export type ChatAssistantBlock = ChatTextBlock | ChatToolUseBlock;

export type ChatMessage =
  | { role: 'user'; content: ChatUserBlock[]; at: number }
  | { role: 'assistant'; content: ChatAssistantBlock[]; at: number };

export type Chat = {
  id: string;
  universeId: string;
  title: string;
  createdAt: number;
  lastUsedAt: number;
  messageCount: number;
};
